/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * CertificateCard
 * ---------------
 * One certificate on "My certificates": who and what, the facts row, the live
 * certificate (the same page the PDF is made from, with the real course map),
 * Download PDF / PNG and Share. Share asks PNG or PDF first, builds that file,
 * then offers what the device can do with it (see certificateShare.js).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../components/feed/FeedParts';
import CertificateRender from '../../../components/athlohive/CertificateRender';
import { buildCertificateFile, saveCertificateFile } from '../../../components/athlohive/certificateExport';
import { loadCertificateMap, useCertificateMap, withCertificateMap } from '../../../components/athlohive/certificateMap';
import { eventLink, shareMessage } from '../../../components/athlohive/myCertificates';
import {
  SHARE_FORMATS, SHARE_TARGETS, TARGETS, canShareFile, copyShareText, shareFile,
} from '../../../components/athlohive/certificateShare';

/** The file for a card in one format, built once per visit (Share and Download reuse it). */
const files = new Map();
export function certificateFile(card, format) {
  const key = `${card.id}:${format}`;
  if (!files.has(key)) {
    const job = (async () => {
      const map = await loadCertificateMap(card.course).catch(() => '');
      return buildCertificateFile({
        templateUrl: card.templateUrl, values: withCertificateMap(card.values, map), format, certificateNo: card.certificateNo,
      });
    })();
    files.set(key, job);
    job.catch(() => files.delete(key));
  }
  return files.get(key);
}

/** Width of an element, kept current. */
function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // The content box: the picture sits inside the box's padding.
    const read = () => {
      const css = window.getComputedStyle(el);
      setWidth(Math.floor(el.clientWidth - parseFloat(css.paddingLeft || 0) - parseFloat(css.paddingRight || 0)));
    };
    read();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

/** True once the element has come near the screen (then stays true). */
function useSeen(ref) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return undefined;
    if (typeof IntersectionObserver !== 'function') { setSeen(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setSeen(true);
    }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, seen]);
  return seen;
}

function LivePicture({ card, width }) {
  const mapImage = useCertificateMap(card.course);
  const values = useMemo(() => withCertificateMap(card.values, mapImage), [card.values, mapImage]);
  return (
    <CertificateRender
      className="fd-cert-frame"
      template={{ name: card.templateName, url: card.templateUrl }}
      width={width}
      values={values}
    />
  );
}

/** The certificate itself, drawn only when its card is near the screen. */
export function CertificatePicture({ card, showNumber = true, onPreview }) {
  const box = useRef(null);
  const width = useWidth(box);
  const seen = useSeen(box);
  const canPreview = Boolean(onPreview && card.templateUrl);
  return (
    <div className={`fd-cert-pic${canPreview ? ' can-preview' : ''}`} ref={box}>
      {!card.templateUrl ? (
        <span className="fd-cert-frame fd-cert-none"><Icon name="error" />Certificate unavailable right now</span>
      ) : seen && width > 0 ? (
        <LivePicture card={card} width={width} />
      ) : (
        <span className="fd-cert-frame fd-skel" />
      )}
      {showNumber && card.certificateNo && <span className="fd-cert-no">No. {card.certificateNo}</span>}
      {canPreview && (
        <button type="button" className="fd-cert-zoom" onClick={onPreview}
          aria-label={`Preview ${card.activity.name} certificate`}>
          <span className="fd-cert-zoom-pill"><Icon name="open_in_full" />Preview</span>
        </button>
      )}
    </div>
  );
}

/**
 * The certificate at full size (up to its own 1123 × 794 page), with Download PDF / PNG. The same
 * live page the card shows and the files are made from. Esc, × or a click outside closes it.
 */
function CertificatePreview({ card, busy, onDownload, onClose }) {
  const closeRef = useRef(null);
  const [size, setSize] = useState(() => previewWidth());

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onResize = () => setSize(previewWidth());
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return (
    <div className="fd-cert-preview" role="presentation" onClick={onClose}>
      <div className="fd-cert-preview-box" role="dialog" aria-modal="true"
        aria-label={`${card.activity.name} certificate`} onClick={(e) => e.stopPropagation()}>
        <div className="fd-cert-preview-h">
          <div style={{ minWidth: 0 }}>
            <b>{card.activity.name}</b>
            <span>{card.name}{card.certificateNo ? ` · No. ${card.certificateNo}` : ''}</span>
          </div>
          <button type="button" className="fd-iconbtn" ref={closeRef} aria-label="Close preview" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        <div className="fd-cert-preview-page" style={{ width: size }}>
          <LivePicture card={card} width={size} />
        </div>
        <div className="fd-cert-preview-act">
          <button type="button" className="fd-btn fd-btn-primary" onClick={() => onDownload('pdf')} disabled={!!busy}>
            <Icon name={busy === 'pdf' ? 'hourglass_top' : 'download'} />{busy === 'pdf' ? 'Preparing PDF…' : 'Download PDF'}
          </button>
          <button type="button" className="fd-btn fd-btn-line" onClick={() => onDownload('png')} disabled={!!busy}>
            <Icon name={busy === 'png' ? 'hourglass_top' : 'image'} />Download PNG
          </button>
        </div>
      </div>
    </div>
  );
}

/** The preview's width: the page's own 1123 px at most, inside the window with room for the bars. */
function previewWidth() {
  if (typeof window === 'undefined') return 900;
  const byWidth = window.innerWidth - 48;
  const byHeight = ((window.innerHeight - 190) * 1123) / 794;
  return Math.max(260, Math.floor(Math.min(1123, byWidth, byHeight)));
}

function ShareMenu({ card, anchor, onClose, onNotice }) {
  const [step, setStep] = useState({ name: 'format' });
  const ref = useRef(null);
  const text = shareMessage(card);
  const link = eventLink(card);
  const title = `${card.activity.name} certificate`;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    // The Share button toggles the menu itself, so a press on it is not "outside".
    const inside = (node) => (ref.current && ref.current.contains(node)) || (anchor.current && anchor.current.contains(node));
    const onDown = (e) => { if (!inside(e.target)) onClose(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, anchor]);

  const pick = async (format) => {
    setStep({ name: 'preparing', format });
    try {
      const file = await certificateFile(card, format);
      const native = canShareFile(file);
      // A desktop browser cannot attach the file to WhatsApp or X: save it so
      // the person can attach it in the app the next step opens.
      if (!native) saveCertificateFile(file);
      setStep({ name: 'ready', format, file, native });
    } catch (_) {
      setStep({ name: 'error', format });
    }
  };

  const openNative = async () => {
    try {
      const done = await shareFile(step.file, { text, link, title });
      if (done) onClose();
    } catch (_) {
      // The share sheet refused (an old browser): fall back to saving the file.
      saveCertificateFile(step.file);
      setStep({ ...step, native: false });
    }
  };

  const openTarget = async (key) => {
    if (key === 'copy') {
      const ok = await copyShareText({ text, link });
      onNotice(ok ? 'Message and link copied' : 'Could not copy. Select the text above instead.');
      if (ok) onClose();
      return;
    }
    window.open(TARGETS[key].url({ text, link, title }), '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <div className="fd-share" role="dialog" aria-label={`Share ${card.activity.name} certificate`} ref={ref}>
      {step.name === 'format' && (
        <>
          <div className="fd-share-h"><b>Share as</b><button type="button" className="fd-iconbtn" aria-label="Close" onClick={onClose}><Icon name="close" /></button></div>
          {Object.entries(SHARE_FORMATS).map(([format, f]) => (
            <button key={format} type="button" className="fd-share-fmt" onClick={() => pick(format)}>
              <span className={`fd-tico${format === 'pdf' ? ' plain' : ''}`}><Icon name={f.icon} /></span>
              <span><b>{f.label}</b><small>{f.hint}</small></span>
            </button>
          ))}
        </>
      )}
      {step.name === 'preparing' && (
        <p className="fd-share-wait" role="status"><Icon name="hourglass_top" />Preparing the {step.format === 'pdf' ? 'PDF' : 'image'}…</p>
      )}
      {step.name === 'error' && (
        <div className="fd-share-wait" role="alert">
          <span><Icon name="error" />The certificate could not be prepared. Try again.</span>
          <button type="button" className="fd-btn-sm" onClick={() => setStep({ name: 'format' })}>Back</button>
        </div>
      )}
      {step.name === 'ready' && (
        <>
          <button type="button" className="fd-share-back" onClick={() => setStep({ name: 'format' })}>
            <Icon name="chevron_left" />{SHARE_FORMATS[step.format].label}
          </button>
          <p className="fd-share-text">{text}</p>
          {step.native ? (
            <button type="button" className="fd-btn fd-btn-primary fd-share-go" onClick={openNative}>
              <Icon name="share" />Share {step.format === 'pdf' ? 'PDF' : 'image'}
            </button>
          ) : (
            <>
              <p className="fd-share-note"><b>{step.file.name}</b> is saved to your downloads. Attach it in the app; the message and link are filled in for you.</p>
              <div className="fd-share-apps">
                {SHARE_TARGETS[step.format].map((key) => (
                  <button key={key} type="button" onClick={() => openTarget(key)}>
                    <Icon name={TARGETS[key].icon} />{TARGETS[key].label}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default function CertificateCard({ card, highlighted = false, onNotice }) {
  const [busy, setBusy] = useState('');
  const [sharing, setSharing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const shareButton = useRef(null);
  const usable = Boolean(card.templateUrl);
  const closeShare = useCallback(() => setSharing(false), []);
  const closePreview = useCallback(() => setPreviewing(false), []);

  const download = async (format) => {
    setBusy(format);
    try {
      saveCertificateFile(await certificateFile(card, format));
      onNotice(`certificate-${card.certificateNo}.${format} downloaded`);
    } catch (_) {
      onNotice('The certificate could not be downloaded. Try again.');
    } finally {
      setBusy('');
    }
  };

  return (
    <article className={`fd-post fd-cert${highlighted ? ' is-hl' : ''}`} id={`certificate-${card.id}`}>
      <div className="fd-ph">
        <span className="fd-avatar">{card.initials}</span>
        <div style={{ minWidth: 0 }}>
          <b>{card.name}</b>
          <span className="m">Issued {card.issued}{card.organizer ? ` · by ${card.organizer}` : ''}</span>
        </div>
        <span className="fd-cert-ok"><Icon name="verified" fill />Completed</span>
      </div>
      <div className="fd-pt">
        <span className="fd-tico"><Icon name={card.activity.icon} /></span>
        <h3>{card.activity.name}</h3>
      </div>
      {card.facts.length > 0 ? (
        <div className="fd-ps">
          {card.facts.map((f) => <span key={f.label}><small>{f.label}</small><b>{f.value}</b></span>)}
        </div>
      ) : <div className="fd-cert-gap" />}
      <CertificatePicture card={card} onPreview={() => setPreviewing(true)} />
      {previewing && (
        <CertificatePreview card={card} busy={busy} onDownload={download} onClose={closePreview} />
      )}
      <div className="fd-cert-act">
        <button type="button" className="fd-btn fd-btn-primary" onClick={() => download('pdf')} disabled={!usable || !!busy}>
          <Icon name={busy === 'pdf' ? 'hourglass_top' : 'download'} />{busy === 'pdf' ? 'Preparing PDF…' : 'Download PDF'}
        </button>
        <button type="button" className="fd-btn fd-btn-line" onClick={() => download('png')} disabled={!usable || !!busy}
          aria-label={`Download ${card.activity.name} certificate as an image`}>
          <Icon name={busy === 'png' ? 'hourglass_top' : 'image'} />PNG
        </button>
        <button type="button" className="fd-btn fd-btn-line" ref={shareButton} onClick={() => setSharing((v) => !v)} disabled={!usable}
          aria-expanded={sharing} aria-label={`Share ${card.activity.name} certificate`}>
          <Icon name="share" /><span className="l">Share</span>
        </button>
        {sharing && <ShareMenu card={card} anchor={shareButton} onClose={closeShare} onNotice={onNotice} />}
      </div>
    </article>
  );
}
